import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import _get from 'lodash/get';
import classnames from 'classnames';
import useGlobalConfig from 'helpers/useGlobalConfig';
import useEntityTypes from 'helpers/useEntityTypes';
import { BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION } from 'pages/Attributes/consts';
import { HOME_URL, ATTRIBUTES_URL, DYNAMIC_PAGES_URL } from 'components/Router/consts';

// Components
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Tooltip from '@material-ui/core/Tooltip';
import Lock from '@material-ui/icons/Lock';
import CheckCircle from '@material-ui/icons/CheckCircle';
import NotInterested from '@material-ui/icons/NotInterested';

import makeStyles from '@material-ui/core/styles/makeStyles';

const USER_EDITABILITY_NONE = 'NONE';
const USER_EDITABILITY_FULL = 'FULL';
const USER_EDITABILITY_PARTIAL = 'PARTIAL';
const LOCKED_TOOLTIP_TITLE = 'This is a Sift default attribute and may not be edited.';

function AttributeRow({ dragIcon, value, list, classes: classesFromProps }) {
  const classes = useStyles();
  const history = useHistory();
  const globalConfig = useGlobalConfig();
  const entityTypes = useEntityTypes();
  const isGeneral = _get(value, 'objectKey', '').includes('general');
  const baseField = _get(value, 'baseField');
  const displayName = _get(value, 'displayName');
  const dataType = _get(globalConfig, `fields.${baseField}.displayName`);
  const objectKey = _get(value, 'objectKey');
  const entityField = _get(value, 'entityField');
  const isNotClickable =
    !_get(value, 'editable') &&
    !_get(value, 'deletable') &&
    ![BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION].includes(_get(value, 'baseField'));

  const renderLockedTooltipCell = data =>
    isNotClickable ? (
      <Tooltip title={LOCKED_TOOLTIP_TITLE}>
        <div>{data}</div>
      </Tooltip>
    ) : (
      data
    );

  const userEditing = () => {
    if (_get(value, '_userEditability') === USER_EDITABILITY_NONE) {
      return (
        <div>
          <FiberManualRecordIcon className={classnames(classes.circleIcon, classes.grayIcon)} />
          Not Editable
        </div>
      );
    } else if (_get(value, '_userEditability') === USER_EDITABILITY_FULL) {
      return (
        <div>
          <FiberManualRecordIcon
            className={classnames(classes.circleIcon, classes.greenCircleIcon)}
          />
          Editable
        </div>
      );
    } else if (_get(value, '_userEditability') === USER_EDITABILITY_PARTIAL) {
      return (
        <div>
          <FiberManualRecordIcon className={classes.circleIcon} color="secondary" /> Partially
          Editable
        </div>
      );
    }
  };

  const enabled =
    !_get(value, 'connectDisabled') && !_get(value, 'deletable') ? (
      <Tooltip title={isNotClickable ? `Locked. ${LOCKED_TOOLTIP_TITLE}` : 'Locked.'}>
        <Lock color="secondary" data-testid={`${displayName}-locked`} />
      </Tooltip>
    ) : !_get(value, 'connectDisabled') && _get(value, 'deletable') ? (
      <Tooltip title={isNotClickable ? `Enabled. ${LOCKED_TOOLTIP_TITLE}` : 'Enabled.'}>
        <CheckCircle color="secondary" data-testid={`${displayName}-enabled`} />
      </Tooltip>
    ) : (
      <Tooltip title={isNotClickable ? `Not Enabled. ${LOCKED_TOOLTIP_TITLE}` : 'Not Enabled'}>
        <NotInterested color="disabled" data-testid={`${displayName}-not enabled`} />
      </Tooltip>
    );

  const handleEditClick = () => {
    if ([BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION].includes(_get(value, 'baseField'))) {
      const entityType = Object.values(entityTypes).find(
        entityType => entityType._personClientFieldObjectKey === value.objectKey,
      );
      history.push({ pathname: `${HOME_URL}${DYNAMIC_PAGES_URL}/${entityType.id}` });
    }
    history.push(`${HOME_URL}${ATTRIBUTES_URL}/${entityField}/edit/${objectKey}`);
  };

  return value ? (
    <TableRow
      className={classnames(classes.row, { [classes.notClickable]: isNotClickable })}
      hover
      onClick={() => (isNotClickable ? null : handleEditClick())}
      data-testid={`${displayName}-row`}
    >
      <TableCell className={classesFromProps.mainCell}>
        {renderLockedTooltipCell(isGeneral ? 'General Section' : displayName)}
      </TableCell>
      <TableCell className={classesFromProps.mainCell}>
        {renderLockedTooltipCell(dataType)}
      </TableCell>
      <TableCell className={classesFromProps.mainCell}>
        {renderLockedTooltipCell(userEditing())}
      </TableCell>
      <TableCell className={classesFromProps.mainCell}>{enabled}</TableCell>
      <TableCell className={classesFromProps.lastCell}>
        {isGeneral ? (
          <Tooltip title="This will be displayed at the top of the Reach Out To Me For section without a category name.">
            <Lock className={classes.grayIcon} data-testid="general lock" />
          </Tooltip>
        ) : list && list.length > 1 ? (
          dragIcon
        ) : null}
      </TableCell>
    </TableRow>
  ) : null;
}

AttributeRow.propTypes = {
  dragIcon: PropTypes.node,
  value: PropTypes.object.isRequired,
  list: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
};

const useStyles = makeStyles(theme => ({
  root: {},
  row: {
    width: '100%',
    cursor: 'pointer',
  },
  circleIcon: {
    width: theme.spacing(),
    height: theme.spacing(),
    marginRight: theme.spacing(0.75),
  },
  grayIcon: {
    color: theme.colors.gray1,
  },
  greenCircleIcon: {
    color: theme.palette.success.main,
  },
  notClickable: {
    cursor: 'default',
  },
}));

export default AttributeRow;
